import React, { useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { OneWordRace } from '@/services/oneWordRacesService';

interface Props {
  races: OneWordRace[];
  topN?: number;
  height?: number;
}

// Distinct colors for per-letter lines (total errors uses a fixed red, below).
const LETTER_COLORS = [
  '#60a5fa', '#34d399', '#fbbf24', '#a78bfa', '#f472b6', '#22d3ee', '#fb923c',
];
const TOTAL_COLOR = '#ef4444';

const fmtLetter = (l: string): string => (l === '_' ? '␣' : l);

/**
 * Per-letter error trend over time across a set of races, plus a red "total errors
 * per race" line. X axis = each race (chronological); Y axis = error count.
 * Shared by the race-complete and training-statistics widgets.
 */
const OneWordLetterErrorChart: React.FC<Props> = ({ races, topN = 7, height = 200 }) => {
  const { data, topLetters } = useMemo(() => {
    // Rank letters by cumulative mistakes across the supplied races.
    const totals: Record<string, number> = {};
    for (const r of races) {
      for (const [letter, count] of Object.entries(r.letterMistakes || {})) {
        totals[letter] = (totals[letter] || 0) + count;
      }
    }
    const ranked = Object.entries(totals)
      .filter(([, c]) => c > 0)
      .sort((a, b) => b[1] - a[1])
      .slice(0, topN)
      .map(([letter]) => letter);

    const points = races.map((r, i) => {
      const point: Record<string, number | string> = {
        idx: i + 1,
        label: new Date(r.completedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        total: r.totalMistakes ?? 0,
      };
      for (const letter of ranked) {
        point[letter] = r.letterMistakes?.[letter] || 0;
      }
      return point;
    });

    return { data: points, topLetters: ranked };
  }, [races, topN]);

  if (!races.length) {
    return <div className="owc-empty">No races recorded yet — finish a race to see trends.</div>;
  }

  return (
    <div className="owc-chart">
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(75,85,99,0.3)" />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} />
          <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#94a3b8' }} />
          <Tooltip
            contentStyle={{
              background: 'rgba(17,24,39,0.95)',
              border: '1px solid rgba(75,85,99,0.5)',
              borderRadius: 8,
              fontSize: 11,
            }}
            labelStyle={{ color: '#cbd5e1' }}
          />
          <Legend wrapperStyle={{ fontSize: 10 }} formatter={(v) => (v === 'total' ? 'Total' : fmtLetter(String(v)))} />
          {topLetters.map((letter, i) => (
            <Line
              key={letter}
              type="monotone"
              dataKey={letter}
              name={letter}
              stroke={LETTER_COLORS[i % LETTER_COLORS.length]}
              strokeWidth={2}
              dot={{ r: 2 }}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          ))}
          <Line
            type="monotone"
            dataKey="total"
            name="total"
            stroke={TOTAL_COLOR}
            strokeWidth={3}
            dot={{ r: 2 }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>

      <style>{`
        .owc-chart { width: 100%; }
        .owc-empty {
          font-size: 0.72rem;
          color: #64748b;
          text-align: center;
          padding: 24px 8px;
        }
      `}</style>
    </div>
  );
};

export default React.memo(OneWordLetterErrorChart);
